/**
 * Component Registry
 *
 * Manages component registration, lookup, and filtering based on maturity and feature flags.
 */

import { FeatureFlags } from "../feature-flags/FeatureFlags";
import debugLogger from "../utils/debugLogger";

import { PlatformConfigManager } from "./PlatformConfig";
import { ComponentMetadata, MaturityLevel } from "./types";

export class ComponentRegistry {
  private platformConfig: PlatformConfigManager;
  private featureFlags: FeatureFlags;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private componentCache: Map<string, any> = new Map();

  constructor(
    platformConfig: PlatformConfigManager,
    featureFlags: FeatureFlags,
  ) {
    this.platformConfig = platformConfig;
    this.featureFlags = featureFlags;
  }

  /**
   * Get component metadata by name
   */
  public getComponent(name: string): ComponentMetadata | undefined {
    return this.platformConfig.getComponent(name);
  }

  /**
   * Get all components for a business domain
   */
  public getComponentsByBusinessDomain(
    businessDomain: string,
  ): ComponentMetadata[] {
    const domain = this.platformConfig.getBusinessDomain(businessDomain);
    if (!domain) return [];

    const components: ComponentMetadata[] = [];
    domain.features.forEach((feature) => {
      components.push(...feature.components);
    });

    return components;
  }

  /**
   * Get all components for a feature
   */
  public getComponentsByFeature(
    businessDomain: string,
    featureName: string,
  ): ComponentMetadata[] {
    const feature = this.platformConfig.getFeature(businessDomain, featureName);
    return feature?.components || [];
  }

  /**
   * Get enabled components for a business domain
   */
  public getEnabledComponents(
    businessDomain: string,
    userId?: string,
  ): ComponentMetadata[] {
    return this.platformConfig.getEnabledComponents(businessDomain, userId);
  }

  /**
   * Get enabled components for a feature
   */
  public getEnabledComponentsByFeature(
    businessDomain: string,
    featureName: string,
    userId?: string,
  ): ComponentMetadata[] {
    const feature = this.platformConfig.getFeature(businessDomain, featureName);
    if (!feature) return [];

    return feature.components.filter((component) =>
      this.platformConfig.isComponentEnabled(component.name, userId),
    );
  }

  /**
   * Check if component is enabled
   */
  public isComponentEnabled(componentName: string, userId?: string): boolean {
    return this.platformConfig.isComponentEnabled(componentName, userId);
  }

  /**
   * Get components filtered by maturity level
   */
  public getComponentsByMaturity(maturity: MaturityLevel): ComponentMetadata[] {
    return this.platformConfig.getComponentsByMaturity(maturity);
  }

  /**
   * Get components filtered by feature flag
   */
  public getComponentsByFeatureFlag(
    flagName: string,
    userId?: string,
  ): ComponentMetadata[] {
    if (!this.featureFlags.isEnabled(flagName, userId)) {
      return [];
    }

    const allComponents = Array.from(
      this.platformConfig.getBusinessDomains(),
    ).flatMap((domain) =>
      domain.features.flatMap((feature) => feature.components),
    );

    return allComponents.filter((component) =>
      component.featureFlags.includes(flagName),
    );
  }

  /**
   * Load component dynamically by name
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async loadComponent(componentName: string): Promise<any> {
    if (this.componentCache.has(componentName)) {
      return this.componentCache.get(componentName);
    }

    const component = this.getComponent(componentName);
    if (!component) {
      throw new Error(`Component '${componentName}' not found in registry`);
    }

    // Check if component is enabled
    if (!this.isComponentEnabled(componentName)) {
      throw new Error(`Component '${componentName}' is not enabled`);
    }

    try {
      // Dynamically import the component
      // Vite requires full path for dynamic imports
      const modulePath = `/src/${component.path}.tsx`;
      const module = await import(/* @vite-ignore */ modulePath);

      // Try to get default export or named export matching component name
      const Component =
        module.default || module[componentName] || module[component.name];

      if (!Component) {
        throw new Error(
          `Component '${componentName}' not found in module '${component.path}'`,
        );
      }

      this.componentCache.set(componentName, Component);
      return Component;
    } catch (error) {
      debugLogger.error(`Failed to load component '${componentName}':`, error);
      throw error;
    }
  }

  /**
   * Clear component cache
   */
  public clearCache(): void {
    this.componentCache.clear();
  }

  /**
   * Get component metadata with full context
   */
  public getComponentMetadata(componentName: string): ComponentMetadata | null {
    return this.getComponent(componentName) || null;
  }

  /**
   * Search components by name pattern
   */
  public searchComponents(pattern: string): ComponentMetadata[] {
    const allComponents = Array.from(
      this.platformConfig.getBusinessDomains(),
    ).flatMap((domain) =>
      domain.features.flatMap((feature) => feature.components),
    );

    const regex = new RegExp(pattern, "i");
    return allComponents.filter(
      (component) =>
        regex.test(component.name) ||
        (component.description && regex.test(component.description)),
    );
  }
}
